import { useCallback, useEffect, useState } from 'react';
import { interveneService } from '../services/interveneService';
import SweetAlert from '../../../components/Shared/Notification';
import { DIALER_CALL_COPY } from '../constants/dialerCallCopy';

const EMPTY_FORM = {
  destination_number: '',
  agent_number: '',
  caller_id: '',
  agent: null,
  tollFree: null,
};

export default function useDialerCall({ open, phoneNumber, onClose }) {
  const [formData, setFormData] = useState({ ...EMPTY_FORM, destination_number: phoneNumber || '' });
  const [errors, setErrors] = useState({});
  const [callerIds, setCallerIds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const loadDialerOptions = async () => {
      try {
        const [callerRes, agentRes] = await Promise.all([
          interveneService.getDialerCallerIds(),
          interveneService.getDialerAgents(),
        ]);

        if (callerRes?.status === 200) {
          setCallerIds(Array.isArray(callerRes.data) ? callerRes.data : []);
        }
        if (agentRes?.status === 200) {
          setAgents(agentRes?.data?.data || []);
        }
      } catch {
        SweetAlert(DIALER_CALL_COPY.notifications.loadFailed, 'error');
      }
    };

    loadDialerOptions();
  }, [open]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      destination_number: phoneNumber || '',
    }));
  }, [phoneNumber]);

  const selectAgent = useCallback((option) => {
    setFormData((prev) => ({
      ...prev,
      agent: option,
      agent_number: option?.follow_me_number || '',
    }));
    setErrors((prev) => ({ ...prev, agent_number: '' }));
  }, []);

  const selectCallerId = useCallback((option) => {
    setFormData((prev) => ({
      ...prev,
      tollFree: option,
      caller_id: option?.tollfree_number || '',
    }));
    setErrors((prev) => ({ ...prev, caller_id: '' }));
  }, []);

  const validateForm = useCallback(() => {
    const nextErrors = {};
    if (!formData.destination_number) {
      nextErrors.destination_number = 'Destination number is required';
    }
    if (!formData.agent_number) {
      nextErrors.agent_number = 'Agent number is required';
    }
    if (!formData.caller_id) {
      nextErrors.caller_id = 'Caller id is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData.agent_number, formData.caller_id, formData.destination_number]);

  const submit = useCallback(async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const res = await interveneService.postDialerCall({
        destination_number: formData.destination_number,
        agent_number: formData.agent_number,
        caller_id: formData.caller_id,
      });

      if (res?.status === 200) {
        SweetAlert(DIALER_CALL_COPY.notifications.success, 'success');
        onClose?.();
      } else {
        throw new Error(DIALER_CALL_COPY.notifications.failed);
      }
    } catch (err) {
      SweetAlert(err.message || DIALER_CALL_COPY.notifications.failed, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onClose, validateForm]);

  return {
    formData,
    errors,
    agents,
    callerIds,
    isSubmitting,
    selectAgent,
    selectCallerId,
    submit,
  };
}
